/**
 * Landing page — driven by content surfaces (`hero` / `about` /
 * `categories` / `build-your-own` / `contact` / `modals`).
 *
 * Redesign pass A (sandbox): a slim sticky header tops the page, the hero
 * CTA points into the builder, and the old full-catalogue dump (box-themes
 * grid + ALL PACKAGING / ALL MERCH / ALL FINAL TOUCH showcases, which
 * consumed the `box-themes` + `section-banners` surfaces) is replaced by
 * How-it-works, a curated Popular-boxes row, a client-logo trust strip and
 * proof points. New marketing copy is hardcoded placeholder data, each
 * flagged `PLACEHOLDER — real content supplied later`. Category/flow
 * wiring (admin-driven tiles, coming-soon row, BYO buttons) is unchanged.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { isHidden, isLive } from '@brandbox/content-schema';
import { FlowConfig } from '../types';
import {
  useFlowSummaries,
  useFlows,
  useIsPreview,
  useLandingContent,
  type CategoryItem,
} from '../lib/content';
import {
  filterButtonsToFlowBacked,
  filterCategoriesToFlowBacked,
  getComingSoonAppendCategories,
  getHeroGridCategoriesWithPreview,
  getLiveAppendCategories,
  overlayAdminHeroImages,
  withPreviewTileInComingSoonRow,
  type FlowVisibilityProjection,
} from '../lib/categories';
import { ContactForm } from './ContactForm';
import { SiteFooter } from './SiteFooter';
import { scrollToSection } from '../lib/scrollToSection';

/**
 * Parse a small subset of HTML — `<p>`, `<strong>`, `<em>` — into React
 * children. Avoids `dangerouslySetInnerHTML` while still letting marketing
 * supply bolded fragments via the about-section content surface.
 *
 * Limited grammar: only handles non-nested `<strong>` and `<em>` inside the
 * outer `<p>...</p>` wrapper (or plain text). Anything else falls through
 * as a plain string node. Marketing input is admin-authored (Entra-gated),
 * not user input, so the tag whitelist is policy not a security boundary.
 */
function renderRichText(html: string): React.ReactNode {
  // Strip the outer <p>...</p> wrapper if present.
  const stripped = html.trim().replace(/^<p>([\s\S]*)<\/p>$/i, '$1');
  // Split on <strong>...</strong> and <em>...</em>; keep delimiters.
  const re = /<(strong|em)>([\s\S]*?)<\/\1>/g;
  const out: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(stripped)) !== null) {
    if (m.index > lastIndex) {
      out.push(stripped.slice(lastIndex, m.index));
    }
    const tag = m[1] as 'strong' | 'em';
    const inner = m[2];
    if (tag === 'strong') {
      out.push(
        <span key={`r${key++}`} className="font-bold">
          {inner}
        </span>,
      );
    } else {
      out.push(<em key={`r${key++}`}>{inner}</em>);
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < stripped.length) out.push(stripped.slice(lastIndex));
  return out;
}

// ---------------------------------------------------------------------
// Redesign pass A — new marketing-section content. Everything below is
// PLACEHOLDER — real content supplied later (copy, client logos, numbers).
// ---------------------------------------------------------------------

// PLACEHOLDER — real content supplied later.
const HOW_IT_WORKS_STEPS = [
  {
    title: 'Pick your theme',
    body: 'Start from a curated box theme and choose the packaging that fits the moment.',
  },
  {
    title: 'Fill and brand it',
    body: 'Select merch from our catalogue and upload your logo; we brand every item and the box.',
  },
  {
    title: 'Get your proposal',
    body: 'Send the request and a tailored quote lands in your inbox within 24 hours.',
  },
];

// PLACEHOLDER — real content supplied later (one tagline per popular box).
const POPULAR_BOX_TAGLINES = [
  'Our most-requested box for seasonal client gifting.',
  'The team favourite for onboarding new colleagues.',
  'A flexible base that works for any campaign.',
];

// PLACEHOLDER — real content supplied later (verified numbers).
const PROOF_POINTS = [
  'Proposals within 24 hours',
  '250+ boxes shipped',
  'EU-wide delivery',
];

interface Props {
  onSelectCategory: (id: string) => void;
  flow: FlowConfig;
}

export function LandingPage({ onSelectCategory }: Props) {
  const hero = useLandingContent('hero');
  const about = useLandingContent('about');
  const { categories: allCategories } = useLandingContent('categories');
  const buildYourOwn = useLandingContent('build-your-own');
  const contactCopy = useLandingContent('contact');
  const modals = useLandingContent('modals').modals;

  // Redesign pass C (item 16) — honour `/#section` deep links (used by the
  // shared footer when navigating back from the builder/checkout/success).
  // The sections render after React mounts, so the browser's native anchor
  // scroll fires too early; re-run it once the landing is on screen.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) scrollToSection(id);
  }, []);

  // The "About THE BRANDBOX STUDIO" section is tucked behind the header
  // "About" tab so "How it works" sits directly below the hero. Hidden by
  // default; the tab reveals it and scrolls to it (the scroll fires once the
  // section has mounted, hence the effect; a re-click while already shown
  // scrolls immediately from the handler).
  const [showAbout, setShowAbout] = useState(false);
  useEffect(() => {
    if (showAbout) scrollToSection('about');
  }, [showAbout]);
  const revealAbout = () => {
    if (showAbout) {
      scrollToSection('about');
      return;
    }
    setShowAbout(true);
  };

  // Slice 5 of the pivot — when the admin is editing a box in the
  // preview iframe and that box doesn't match any existing landing
  // category, synthesize an appended tile so the operator sees their
  // in-progress box materialize on the public landing as if it had
  // already shipped. Outside preview mode this is a no-op (production
  // rendering for real customers stays bit-for-bit identical —
  // briefing Constraint #1).
  //
  // Slice 5 trade-off: in preview mode the tile always lands in the
  // Coming Soon row, never in the main hero grid — even when the
  // in-progress flow has `visibility: 'live'`. Preview is for
  // editorial work; coming-soon styling makes the tile read as
  // unmistakably "draft" so the operator never confuses preview-tile
  // with shipped-tile.
  //
  // The flow id is the key of the `flowsRegistry` Record; the public's
  // FlowConfig type doesn't carry it as a field, so we read via the
  // entry key here.
  const isPreview = useIsPreview();
  const flowsRegistry = useFlows();
  const previewTile: CategoryItem | null = useMemo(() => {
    if (!isPreview) return null;
    const knownIds = new Set(allCategories.map((c) => c.id));
    const entry = Object.entries(flowsRegistry).find(
      ([id]) => id && !knownIds.has(id),
    );
    if (!entry) return null;
    const [previewId, previewFlow] = entry;
    const title =
      (previewFlow as { title?: string }).title ?? previewId;
    // `isAvailable: false` — Slice 5 styles the preview tile as a
    // Coming Soon entry (greyed + pill). The hero grid no longer
    // receives this tile (the call below passes `null`).
    return {
      id: previewId,
      label: title.toUpperCase(),
      image:
        (previewFlow as { heroImage?: string }).heroImage ??
        '/images/general/placeholder.webp',
      isAvailable: false,
      order: 9999,
    } satisfies CategoryItem;
  }, [isPreview, allCategories, flowsRegistry]);

  // Slice 2 pivot — fold the visibility-driven appends in:
  //   * `live` flows not already in landing-categories.json land in the
  //     main hero grid (clickable, no greyed pill)
  //   * `coming-soon` flows that aren't already in the static list
  //     render in a SEPARATE Coming Soon row below the existing grid
  //     (with the greyed pill, not clickable). The row only renders
  //     when there's at least one dynamic entry — zero-flow case
  //     keeps the visual baseline byte-identical (briefing #1).
  const flowSummaries = useFlowSummaries();
  const flowProjections = useMemo<FlowVisibilityProjection[]>(
    () =>
      flowSummaries
        .filter((s) => !isHidden(s))
        .map((s) => ({
          id: s.id,
          title: s.title,
          visibility: s.visibility as 'live' | 'coming-soon',
          ...(s.heroImage ? { heroImage: s.heroImage } : {}),
        })),
    [flowSummaries],
  );

  // N5 owner rule (2026-06-09) — "no box tile without a backing flow".
  //
  // The `categories` content surface is the LIVE Cosmos doc, which on
  // production still carries 4 stale seed entries (eventkit / worldcup /
  // emergency / onboarding) that have NO matching admin flow. They render
  // as phantom "COMING SOON" tiles even though they aren't boxes in admin.
  // The owner rule: a tile may render ONLY if its id is backed by a real,
  // non-hidden flow in the registry (`flowProjections`). We prune the
  // surface here, BEFORE the hero/box-themes grids consume it, so every
  // downstream tile surface inherits the de-phantomed list in one place.
  //
  // The `categories` surface still supplies curated label/order/hero for
  // ids that DO have a backing flow (spring / office) — see
  // `overlayAdminHeroImages` below. Fallback: when the API is unreachable
  // / pre-hydration, `flowProjections` is empty and the helper returns the
  // static list unchanged so the landing never blanks (Constraint #1).
  const flowBackedCategories = useMemo(
    () => filterCategoriesToFlowBacked(allCategories, flowProjections),
    [allCategories, flowProjections],
  );

  // M+5.7: overlay admin-uploaded hero images on top of the static
  // categories. When an admin uploads a new hero for `spring` or
  // `office`, it shows up immediately on the public landing; if no
  // admin hero is set, the static JSON `image` keeps rendering as the
  // safety net. The label / order / availability stay on the JSON
  // config (the "I really mean it" surface).
  const categoriesWithAdminHero = useMemo(
    () => overlayAdminHeroImages(flowBackedCategories, flowProjections),
    [flowBackedCategories, flowProjections],
  );

  // Slice 5 — the hero grid no longer receives the preview tile (it
  // lands in the Coming Soon row below). We keep
  // `getHeroGridCategoriesWithPreview` callable with `null` so the
  // existing visibility-driven append logic below still works and the
  // shape of this expression stays identical to pre-Slice-5 for the
  // production code-path.
  const heroBase = getHeroGridCategoriesWithPreview(categoriesWithAdminHero, null);

  const liveAppends = useMemo(
    () => getLiveAppendCategories(categoriesWithAdminHero, flowProjections),
    [categoriesWithAdminHero, flowProjections],
  );
  const comingSoonAppendsBase = useMemo(
    () => getComingSoonAppendCategories(categoriesWithAdminHero, flowProjections),
    [categoriesWithAdminHero, flowProjections],
  );

  // Slice 5 — when the admin is editing a brand-new flow that isn't yet
  // in the API-driven coming-soon list, append the preview tile to this
  // row so it visualizes in the same place it'll land once the operator
  // toggles visibility to 'coming-soon'. De-dupe against any entry the
  // API already returned (covers the case where the preview flow also
  // exists in Cosmos as visibility:'coming-soon'). The helper is unit-
  // tested in `categories.test.ts`.
  const comingSoonAppends = useMemo(
    () => withPreviewTileInComingSoonRow(comingSoonAppendsBase, previewTile),
    [comingSoonAppendsBase, previewTile],
  );

  // The hero grid (top of the page) = static categories + admin preview
  // tile + any visibility:'live' Cosmos flow not already in the static
  // list. The Coming Soon row (after the hero) holds the dynamic
  // visibility:'coming-soon' tiles.
  const categories = useMemo(
    () => [...heroBase, ...liveAppends],
    [heroBase, liveAppends],
  );

  // M+5.8 — Build-Your-Own buttons are now admin-driven. The static
  // `landing-build-your-own.json` carries only the always-available
  // spring + office entries; every other admin flow (live or coming
  // soon, not hidden/archived) appends to the button row using the
  // same isAvailable semantics as the hero grid:
  //
  //   * visibility=live      → clickable button (isAvailable=true)
  //   * visibility=coming-soon → COMING SOON pill, disabled (isAvailable=false)
  //   * visibility=hidden    → not present (projection already filtered)
  //
  // De-dupe against the JSON list so an admin flow with id `spring`
  // doesn't render twice. When the API has nothing for us, the JSON
  // baseline still renders bit-for-bit identically.
  const buildYourOwnButtons = useMemo(() => {
    // N5 owner rule — the Build-Your-Own button row is a box-tile surface
    // too (each button calls `onSelectCategory(btn.id)`), so prune any
    // static button whose id has no backing flow. The live `build-your-own`
    // Cosmos surface can carry the same phantom ids as the categories
    // surface; a button that targets `eventkit`/`onboarding` etc. with no
    // admin flow behind it must not render. Empty-registry fallback returns
    // the static buttons unchanged (Constraint #1).
    const staticButtons = filterButtonsToFlowBacked(
      buildYourOwn.buttons,
      flowProjections,
    );
    const knownIds = new Set(staticButtons.map((b) => b.id));
    const dynamic = flowProjections
      .filter((f) => !knownIds.has(f.id))
      .map((f) => ({
        id: f.id,
        label: (f.title ?? f.id).toUpperCase(),
        isAvailable: isLive(f),
      }));
    return [...staticButtons, ...dynamic];
  }, [buildYourOwn.buttons, flowProjections]);

  // Redesign pass A — the curated "Popular boxes" row shows the first
  // three LIVE boxes from the same content-driven category list as the
  // theme grid (so admin-published boxes flow through automatically);
  // the taglines are placeholder marketing copy until real content lands.
  const popularBoxes = useMemo(
    () => categories.filter((c) => c.isAvailable).slice(0, 3),
    [categories],
  );

  return (
    <div className="min-h-screen flex flex-col">
      {/* Slim sticky header — persistent way into the builder. */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-[90%] mx-auto h-16 flex items-center justify-between gap-4">
          <a
            href="/"
            className="flex items-baseline gap-1.5 font-century font-bold text-xs md:text-base tracking-[0.02em] sm:tracking-[0.1em] md:tracking-[0.15em] text-[#444444] whitespace-nowrap"
          >
            {hero.title}
            <span aria-hidden="true" className="w-2 h-2 gradient-dot" />
          </a>
          <nav aria-label="Main" className="flex items-center gap-5 md:gap-8">
            <button
              type="button"
              onClick={() => scrollToSection('how-it-works')}
              className="hidden md:inline-block font-century text-sm text-[#444444]/70 hover:text-[#356d4e] transition-colors"
            >
              How it works
            </button>
            <button
              type="button"
              onClick={revealAbout}
              className="hidden md:inline-block font-century text-sm text-[#444444]/70 hover:text-[#356d4e] transition-colors"
            >
              About
            </button>
            {/* Content-driven marketing links, minus any "contact" cta — the
                header's Contact entry points at the on-page form instead. */}
            {hero.ctas
              .filter((cta) => !/contact/i.test(cta.label))
              .map((cta) => (
                <a
                  key={cta.href}
                  href={cta.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-block font-century text-sm text-[#444444]/70 hover:text-[#356d4e] transition-colors"
                >
                  {cta.label}
                </a>
              ))}
            <button
              type="button"
              onClick={() => scrollToSection('contact')}
              className="hidden sm:inline-block font-century text-sm text-[#444444]/70 hover:text-[#356d4e] transition-colors"
            >
              Contact
            </button>
            <button
              type="button"
              onClick={() => scrollToSection('choose-your-box')}
              className="px-3 sm:px-4 md:px-7 py-2.5 rounded-full bg-[#356d4e] text-white font-century text-[11px] md:text-xs font-bold tracking-wide sm:tracking-widest uppercase hover:bg-[#356d4e]/90 transition-colors shadow-sm whitespace-nowrap"
            >
              Build your box
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative flex flex-col items-center justify-center pt-20 pb-24 px-6 bg-white overflow-hidden">
        {/* Redesign pass C (item 17) — the hero keeps ONE deliberate
            gradient-sphere moment (two spheres framing the title); the
            lower pair that bled behind the category grid is gone. */}
        <div className="sphere-gradient w-[80vw] h-[80vw] max-w-96 max-h-96 -top-20 -right-[10%] " />
        <div className="sphere-gradient w-[70vw] h-[70vw] max-w-80 max-h-80 top-[10%] -left-[10%] " />

        {/* Title Section */}
        <div className="relative z-10 flex flex-col items-center mb-12 space-y-6 max-w-[90%] mx-auto text-center">
          <div className="flex flex-col items-center gap-2">
            <h1 className="text-[clamp(2rem,8vw,4.5rem)] font-black font-century tracking-[0.1em] text-[#444444] flex items-baseline justify-center">
              {hero.title}
              <span aria-hidden="true" className="ml-1 w-3 h-3 md:w-4 md:h-4 gradient-dot" />
            </h1>
            <div className="w-[80%] h-[4px] bg-linear-to-r from-[#356d4e] to-[#57c858] rounded-full" />
          </div>
          <p className="text-[#444444] text-sm md:text-base font-century tracking-wide">
            {hero.subtitle}
          </p>

          {/* Primary CTA points into the builder (theme selection below);
              the marketing links are demoted to quiet secondary links. */}
          <div className="flex flex-col items-center gap-5 pt-2">
            <button
              type="button"
              onClick={() => scrollToSection('choose-your-box')}
              className="px-16 py-4 rounded-full bg-linear-to-r from-[#356d4e] to-[#57c858] text-white font-century text-base font-bold tracking-widest uppercase shadow-lg shadow-[#356d4e]/20 hover:scale-105 transition-transform flex items-center gap-3"
            >
              Build your box
              <span aria-hidden="true">→</span>
            </button>
            <div className="flex items-center gap-8">
              {hero.ctas.map((cta) => (
                <a
                  key={cta.href}
                  href={cta.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-century text-sm text-[#444444]/70 underline underline-offset-4 decoration-[#57c858]/60 hover:text-[#356d4e] transition-colors"
                >
                  {cta.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Theme selection — where the primary CTA lands. */}
        <p
          id="choose-your-box"
          className="relative z-10 font-century text-xs font-bold tracking-[0.25em] uppercase text-[#356d4e] mb-8 flex items-baseline gap-2 scroll-mt-24"
        >
          Choose your box
          <span aria-hidden="true" className="w-1.5 h-1.5 gradient-dot" />
        </p>

        {/* Category Grid - 2x2 Layout */}
        <div
          data-testid="hero-category-grid"
          className="relative z-10 w-full max-w-[90%] grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 px-4"
        >
          {categories.map((cat, idx) => (
            <div
              // m-1 (M+2 hardener): defensive fallback when API returns
              // a category without an id (or with an empty id). Prevents
              // React's "two children with the same key ''" warning.
              key={cat.id || `category-${idx}`}
              className={`group relative overflow-hidden rounded-2xl shadow-sm transition-all duration-500 ${
                cat.isAvailable
                  ? 'cursor-pointer hover:-translate-y-1 hover:shadow-xl'
                  : 'cursor-not-allowed opacity-60 grayscale-[0.5]'
              }`}
              onClick={() => cat.isAvailable && onSelectCategory(cat.id)}
              aria-disabled={!cat.isAvailable}
              title={cat.isAvailable ? undefined : 'Coming soon'}
            >
              <div className="w-full aspect-video overflow-hidden relative">
                {!cat.isAvailable && (
                  <div className="absolute top-4 left-4 z-30">
                    <span className="bg-[#57c858] text-white text-[10px] font-black px-2 py-1 rounded-md tracking-widest uppercase shadow-lg">
                      COMING SOON
                    </span>
                  </div>
                )}
                <img
                  src={cat.image}
                  alt={cat.label}
                  width={600}
                  height={400}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  style={cat.id === 'eventkit' ? { objectPosition: 'center 95%' } : undefined}
                  referrerPolicy="no-referrer"
                />
                {/* Redesign pass A — bottom-up scrim with the label anchored
                    lower-left, replacing the flat 20% black overlay + centered
                    label. Hover feedback lightens the scrim (never zooms the
                    image). */}
                <div
                  aria-hidden="true"
                  className={`absolute inset-0 bg-linear-to-t from-black/60 via-black/15 to-transparent transition-colors duration-500 ${cat.isAvailable ? 'group-hover:from-black/50' : ''}`}
                />
                <span className="absolute bottom-4 left-5 z-20 text-2xl md:text-3xl font-bold tracking-[0.08em] text-white font-century uppercase">
                  {cat.label}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/*
          * Slice 2 pivot — Coming Soon row.
          *
          * Renders ONLY when there's at least one dynamic
          * visibility:'coming-soon' Cosmos flow that isn't already in
          * `landing-categories.json`. The empty-case branch returns
          * `null` so the DOM is bit-for-bit identical to the
          * pre-Slice-2 build (briefing Constraint #1 — no visual
          * regression on the live site).
          *
          * Visual treatment matches the hero grid's "Coming Soon"
          * tiles (same pill + image + label) so we don't introduce a
          * new visual primitive.
          */}
        {comingSoonAppends.length > 0 && (
          <div
            data-testid="coming-soon-row"
            className="relative z-10 w-full max-w-[90%] grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 px-4"
          >
            {comingSoonAppends.map((cat, idx) => (
              <div
                key={cat.id || `coming-soon-${idx}`}
                className="group relative overflow-hidden rounded-2xl shadow-sm transition-all duration-500 cursor-not-allowed opacity-60 grayscale-[0.5]"
                aria-disabled={true}
                title="Coming soon"
              >
                <div className="w-full aspect-video overflow-hidden relative">
                  <div className="absolute top-4 left-4 z-30">
                    <span className="bg-[#57c858] text-white text-[10px] font-black px-2 py-1 rounded-md tracking-widest uppercase shadow-lg">
                      COMING SOON
                    </span>
                  </div>
                  <img
                    src={cat.image}
                    alt={cat.label}
                    width={600}
                    height={400}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                  {/* Redesign pass A — same bottom-up scrim + lower-left label
                      treatment as the hero grid tiles. */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-linear-to-t from-black/60 via-black/15 to-transparent"
                  />
                  <span className="absolute bottom-4 left-5 z-20 text-2xl md:text-3xl font-bold tracking-[0.08em] text-white font-century uppercase">
                    {cat.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* Section: How it works — redesign pass A.
          Replaces the start of the old full-catalogue dump with a tight
          3-step explanation of the journey. Copy is placeholder-flagged. */}
      <section
        id="how-it-works"
        className="w-full bg-[#fbf7f1] py-24 relative overflow-hidden scroll-mt-16"
      >
        <div className="max-w-[90%] mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold font-century text-[#444444] tracking-tight flex items-baseline gap-3 mb-16">
            HOW IT WORKS
            <span aria-hidden="true" className="w-3 h-3 md:w-4 md:h-4 gradient-dot" />
          </h2>
          {/* The numbers ARE the content here — this section is a sequence. */}
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-14">
            {HOW_IT_WORKS_STEPS.map((step, i) => (
              <li key={step.title} className="flex flex-col gap-4">
                <span
                  aria-hidden="true"
                  className="w-12 h-12 rounded-full bg-linear-to-br from-[#356d4e] to-[#57c858] text-white font-century font-bold text-lg flex items-center justify-center shadow-md"
                >
                  {i + 1}
                </span>
                <h3 className="text-2xl font-bold font-century text-[#444444]">
                  {step.title}
                </h3>
                <p className="text-[#444444]/80 text-base leading-relaxed font-century max-w-[44ch]">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Section: Popular boxes — redesign pass A.
          A curated row of live boxes (content-driven) replacing the old
          ALL PACKAGING / ALL MERCH / ALL FINAL TOUCH catalogue dump.
          Taglines are placeholder-flagged. */}
      {popularBoxes.length > 0 && (
        <section className="w-full bg-white py-24 relative overflow-hidden border-t border-gray-100">
          <div className="max-w-[90%] mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
              <h2 className="text-4xl md:text-6xl font-bold font-century text-[#444444] tracking-tight flex items-baseline gap-3">
                POPULAR BOXES
                <span aria-hidden="true" className="w-3 h-3 md:w-4 md:h-4 gradient-dot" />
              </h2>
              <button
                type="button"
                onClick={() => scrollToSection('choose-your-box')}
                className="self-start md:self-auto font-century text-sm font-bold text-[#356d4e] underline underline-offset-4 decoration-[#57c858]/60 hover:decoration-[#57c858] transition-colors"
              >
                See all box themes
              </button>
            </div>
            <div
              data-testid="popular-boxes-row"
              className="grid grid-cols-1 md:grid-cols-3 gap-10"
            >
              {popularBoxes.map((box, idx) => (
                <div
                  key={box.id || `popular-${idx}`}
                  className="group cursor-pointer flex flex-col gap-5"
                  onClick={() => onSelectCategory(box.id)}
                >
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-sm group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300">
                    <img
                      src={box.image}
                      alt={box.label}
                      width={600}
                      height={450}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 bg-linear-to-t from-black/60 via-black/15 to-transparent group-hover:from-black/50 transition-colors duration-300"
                    />
                    <span className="absolute bottom-4 left-5 text-xl md:text-2xl font-bold tracking-[0.08em] text-white font-century uppercase">
                      {box.label}
                    </span>
                  </div>
                  <p className="text-[#444444]/75 text-base leading-relaxed font-century">
                    {/* PLACEHOLDER — real content supplied later. */}
                    {POPULAR_BOX_TAGLINES[idx % POPULAR_BOX_TAGLINES.length]}
                  </p>
                  <span className="font-century text-sm font-bold text-[#356d4e] flex items-center gap-2">
                    Build this box
                    <span
                      aria-hidden="true"
                      className="transition-transform group-hover:translate-x-1"
                    >
                      →
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Section: Trust strip + proof points — redesign pass A.
          PLACEHOLDER — real client logos and verified numbers supplied later. */}
      <section className="w-full bg-white py-16 border-t border-gray-100">
        <div className="max-w-[90%] mx-auto flex flex-col gap-12">
          <div
            data-testid="proof-points"
            className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4"
          >
            {PROOF_POINTS.map((point) => (
              <span
                key={point}
                className="flex items-baseline gap-3 font-century text-base md:text-lg font-bold text-[#356d4e]"
              >
                <span aria-hidden="true" className="w-2 h-2 gradient-dot" />
                {point}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2: Build Your Own Box */}
      <section className="w-full bg-white py-24 relative overflow-hidden">
        <div className="max-w-[90%] mx-auto relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-start mb-24">
            <div className="space-y-4">
              <h2 className="text-5xl md:text-8xl font-century text-[#444444] leading-none flex flex-wrap items-baseline gap-x-4 md:gap-x-6">
                <span className="font-bold uppercase tracking-tighter">
                  {buildYourOwn.eyebrow}
                </span>
                <span className="font-bold italic uppercase tracking-tighter">
                  {buildYourOwn.titleWord}
                </span>
                <span className="font-bold uppercase tracking-tighter">
                  {buildYourOwn.titleSuffix}
                </span>
                <span className="w-3 h-3 md:w-5 md:h-5 rounded-full bg-linear-to-br from-[#57c858] to-[#356d4e] inline-block" />
              </h2>
              <p className="text-[#444444] text-xl md:text-2xl font-century opacity-60">
                {buildYourOwn.caption}
              </p>
            </div>

            {/* Graphic Element - Overlapping Circles */}
            <div className="hidden md:block relative w-80 h-80">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full bg-linear-to-br from-[#57c858] to-[#356d4e] shadow-xl z-10" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full border-2 border-[#57c858] z-0" />
              <div className="absolute top-1/2 left-1/2 -translate-x-[35%] -translate-y-[35%] w-72 h-72 rounded-full border-2 border-[#57c858] z-0" />
            </div>
          </div>

          {/* Button Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="byo-buttons">
            {buildYourOwnButtons.map((btn, idx) => (
              <button
                // m-1 (M+2 hardener): defensive fallback for empty/missing ids.
                key={btn.id || `byo-${idx}`}
                onClick={() => btn.isAvailable && onSelectCategory(btn.id)}
                disabled={!btn.isAvailable}
                aria-disabled={!btn.isAvailable}
                title={btn.isAvailable ? undefined : 'Coming soon'}
                className={`group relative flex items-center justify-center px-10 py-6 rounded-full border text-xl transition-all duration-300 font-century shadow-sm ${
                  btn.isAvailable
                    ? 'border-gray-200 text-[#444444] hover:border-[#57c858] hover:text-[#57c858] hover:shadow-md bg-white'
                    : 'border-gray-100 text-gray-400 bg-gray-50/50 cursor-not-allowed opacity-60'
                }`}
              >
                {!btn.isAvailable && (
                  <span className="absolute -top-2 left-6 bg-[#57c858] text-white text-[8px] font-black px-2 py-0.5 rounded-full tracking-widest uppercase shadow-sm z-10">
                    COMING SOON
                  </span>
                )}
                {btn.label}
                {btn.isAvailable && (
                  <span className="ml-3 transition-transform group-hover:translate-x-2">→</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Section: About — moved out of the top flow (redesign follow-up).
          Hidden by default; revealed + scrolled-to via the header "About"
          tab so "How it works" sits directly below the hero. */}
      {showAbout && (
        <section
          id="about"
          className="w-full bg-linear-to-r from-[#356d4e] to-[#57c858] py-24 overflow-hidden scroll-mt-16"
        >
          <div className="max-w-[90%] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-24 items-start">
            {/* Left Half */}
            <div className="flex flex-col space-y-12">
              <h2 className="flex flex-col text-white leading-[0.9] font-century">
                <span className="text-[clamp(2.5rem,6vw,4.5rem)] font-normal">
                  {about.titleLine1}
                </span>
                <span className="text-[clamp(3.5rem,12vw,7.5rem)] font-bold tracking-tighter uppercase">
                  {about.titleLine2}
                </span>
                <span className="text-[clamp(2.5rem,6vw,4.5rem)] font-normal flex items-baseline">
                  {about.titleLine3}
                  <span className="ml-4 w-4 h-4 md:w-6 md:h-6 rounded-full bg-linear-to-br from-[#57c858] to-[#356d4e] inline-block" />
                </span>
              </h2>

              <div className="w-full md:w-4/5">
                <p className="text-white text-xl leading-relaxed font-light">
                  {renderRichText(about.paragraph1Html)}
                </p>
              </div>
            </div>

            {/* Right Half */}
            <div className="flex flex-col space-y-12">
              <div className="w-full md:w-4/5 aspect-[16/10] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={about.image}
                  alt={about.imageAlt}
                  width={800}
                  height={500}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="w-full md:w-4/5">
                <p className="text-white text-xl leading-relaxed font-light">
                  {renderRichText(about.paragraph2Html)}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Section: Contact Us */}
      <section
        id="contact"
        className="w-full bg-white py-32 relative overflow-hidden border-t border-gray-100 scroll-mt-16"
      >
        <div className="max-w-[90%] mx-auto relative z-10 flex flex-col lg:flex-row items-start justify-between gap-20">
          <div className="flex-1 pt-10">
            <h2 className="text-6xl md:text-8xl font-bold font-century tracking-tighter text-[#444444] flex items-baseline gap-4">
              {contactCopy.heading}
              <span className="w-4 h-4 md:w-6 md:h-6 rounded-full bg-linear-to-br from-[#57c858] to-[#356d4e] inline-block" />
            </h2>
          </div>

          <div className="w-full lg:w-[550px] bg-linear-to-br from-[#356d4e] to-[#57c858] rounded-[32px] p-10 md:p-12 shadow-2xl text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-century mb-10 tracking-tight">
              CONTACT FORM
            </h3>

            {/* Redesign pass A — the form POSTs through the /api/send
                pipeline with inline validation + in-page success/error
                states (see ContactForm.tsx). No mailto:, no alert(). */}
            <ContactForm
              contactEmail="promotions@ddg-europe.com"
              thanks={modals.contactFormThanks}
            />
          </div>
        </div>
      </section>

      {/* Redesign pass C (item 16) — shared on-brand footer. */}
      <SiteFooter />
    </div>
  );
}
